import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Tag, Space, Modal, Form, message, Popconfirm, Select, Badge, Drawer, Spin, Typography, Progress, Card, Tooltip, Divider, Timeline, Tabs } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, UserOutlined, SendOutlined, ThunderboltOutlined, RobotOutlined, HistoryOutlined, ReloadOutlined, RocketOutlined, GlobalOutlined, BookOutlined, ShoppingOutlined, MessageOutlined } from '@ant-design/icons';
import { contactsApi } from '../services/api';
import ReactMarkdown from 'react-markdown';

const { Title, Text, Paragraph } = Typography;
const API_BASE = '/api/v1';

const PLATFORM_CONFIG = {
  ALL:       { label: '전체',      color: '#1890ff', emoji: '🌐' },
  REAL:      { label: 'REAL',      color: '#722ed1', emoji: '📚' },
  TRADESAGA: { label: 'TradeSaga', color: '#f5222d', emoji: '📈' },
  WORKON:    { label: 'WorkOn',    color: '#13c2c2', emoji: '💼' },
  TALK:      { label: 'Talk',      color: '#52c41a', emoji: '💬' },
};

// 사용자 유형 설정
const USER_TYPE_CONFIG = {
  power_user: { label: '🔥 파워유저', color: '#f5222d', bg: '#fff1f0', border: '#ffa39e' },
  normal:     { label: '👤 일반', color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff' },
  at_risk:    { label: '⚠️ 이탈위험', color: '#fa8c16', bg: '#fff7e6', border: '#ffd591' },
  new_user:   { label: '🆕 신규', color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f' },
  dormant:    { label: '💤 휴면', color: '#8c8c8c', bg: '#fafafa', border: '#d9d9d9' },
};

// 액션 타입 설정
const ACTION_TYPE_CONFIG = {
  premium_upsell:   { icon: '💎', label: 'Premium 전환 제안', color: '#722ed1' },
  retention:        { icon: '🔄', label: '리텐션 캠페인', color: '#fa8c16' },
  welcome:          { icon: '👋', label: '웰컴 가이드', color: '#52c41a' },
  reactivation:     { icon: '⚡', label: '재활성화', color: '#f5222d' },
  feature_guide:    { icon: '📖', label: '기능 안내', color: '#1890ff' },
  referral_program: { icon: '🎁', label: '추천 프로그램', color: '#eb2f96' },
  personal_outreach:{ icon: '✉️', label: '개인 연락', color: '#13c2c2' },
};

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('ALL');
  const [platformCounts, setPlatformCounts] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  // Insight drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightData, setInsightData] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [activeTab, setActiveTab] = useState('insight');

  // Action execution state
  const [actionLoading, setActionLoading] = useState(null);
  const [actionResults, setActionResults] = useState({});

  // History state
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Batch analysis state
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchSummary, setBatchSummary] = useState(null);
  const [agentRuns, setAgentRuns] = useState([]);

  useEffect(() => { fetchContacts(); }, [pagination.current, searchText, selectedPlatform]);
  useEffect(() => { fetchPlatformCounts(); }, []);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
        search: searchText || undefined,
      };
      if (selectedPlatform !== 'ALL') {
        params.platform = selectedPlatform;
      }
      const res = await contactsApi.list(params);
      setContacts(res.data.items);
      setPagination(p => ({ ...p, total: res.data.total }));
    } catch { message.error('연락처 로딩 실패'); }
    setLoading(false);
  };

  const fetchPlatformCounts = async () => {
    try {
      // Get counts from dashboard stats
      const res = await fetch(`${API_BASE}/dashboard/stats`);
      if (res.ok) {
        const data = await res.json();
        const counts = { ALL: data.total_contacts || 0 };
        if (data.platform_breakdown) {
          Object.entries(data.platform_breakdown).forEach(([k, v]) => {
            counts[k.toUpperCase()] = v;
          });
        }
        setPlatformCounts(counts);
      }
    } catch { /* fallback - counts will show 0 */ }
  };

  const handleDelete = async (id) => {
    try { await contactsApi.delete(id); message.success('삭제 완료'); fetchContacts(); fetchPlatformCounts(); }
    catch { message.error('삭제 실패'); }
  };

  const handleAdd = async (values) => {
    try { await contactsApi.create(values); message.success('추가 완료'); setIsModalOpen(false); form.resetFields(); fetchContacts(); fetchPlatformCounts(); }
    catch { message.error('추가 실패'); }
  };

  // ── 인사이트 드로어 열기 ──
  const handleRowClick = async (contact) => {
    setSelectedContact(contact);
    setDrawerOpen(true);
    setInsightLoading(true);
    setInsightData(null);
    setActionResults({});
    setActiveTab('insight');
    setHistoryData(null);
    try {
      const res = await fetch(`${API_BASE}/insights/${contact.id}`);
      const data = await res.json();
      setInsightData(data);
    } catch {
      message.error('인사이트 로딩 실패');
    }
    setInsightLoading(false);
  };

  // ── 추천 액션 실행 ──
  const handleExecuteAction = async (actionData, index, autoSend = false) => {
    if (!insightData) return;
    setActionLoading(index);
    try {
      const res = await fetch(`${API_BASE}/insights/${selectedContact.id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: actionData,
          insight_id: insightData.insight_id,
          auto_send: autoSend,
        }),
      });
      const result = await res.json();
      if (result.success) {
        setActionResults(prev => ({ ...prev, [index]: result }));
        message.success(result.message);
      } else {
        message.error(result.detail || '액션 실행 실패');
      }
    } catch {
      message.error('액션 실행 중 오류');
    }
    setActionLoading(null);
  };

  // ── 인사이트 이력 로드 ──
  const loadHistory = async () => {
    if (!selectedContact) return;
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_BASE}/insights/${selectedContact.id}/history`);
      setHistoryData(await res.json());
    } catch { message.error('이력 로딩 실패'); }
    setHistoryLoading(false);
  };

  // ── 일괄 분석 ──
  const handleBatchAnalyze = async () => {
    setBatchRunning(true);
    try {
      const body = { run_type: 'manual' };
      if (selectedPlatform !== 'ALL') body.platform = selectedPlatform;
      const res = await fetch(`${API_BASE}/insights/batch/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        message.success(`${selectedPlatform === 'ALL' ? '전체' : PLATFORM_CONFIG[selectedPlatform].label} 일괄 분석 시작!`);
        setTimeout(loadBatchSummary, 3000);
      }
    } catch { message.error('일괄 분석 시작 실패'); }
    setBatchRunning(false);
  };

  const loadBatchSummary = async () => {
    try {
      const platformParam = selectedPlatform !== 'ALL' ? `?platform=${selectedPlatform}` : '';
      const [summaryRes, runsRes] = await Promise.all([
        fetch(`${API_BASE}/insights/batch/summary${platformParam}`),
        fetch(`${API_BASE}/insights/batch/runs`),
      ]);
      setBatchSummary(await summaryRes.json());
      setAgentRuns(await runsRes.json());
    } catch { /* ignore */ }
  };

  // ── 플랫폼 태그 색상 ──
  const getPlatformTagColor = (tag) => {
    const t = tag.toLowerCase();
    if (t === 'real') return '#722ed1';
    if (t === 'tradesaga' || t === 'tradesaga_user') return '#f5222d';
    if (t === 'workon' || t.startsWith('workon_')) return '#13c2c2';
    if (t === 'talk') return '#52c41a';
    if (t === 'synced') return '#8c8c8c';
    return 'default';
  };

  // ── 테이블 컬럼 ──
  const columns = [
    { title: '이메일', dataIndex: 'email', ellipsis: true },
    { title: '이름', dataIndex: 'name', width: 120, render: (n) => n || '-' },
    {
      title: '플랫폼', dataIndex: 'tags', width: 120,
      render: (tags) => {
        if (!tags) return '-';
        // Extract platform tags
        const platformTags = tags.filter(t => ['real', 'tradesaga', 'workon', 'talk'].includes(t.toLowerCase()));
        if (platformTags.length === 0) return '-';
        return platformTags.map(t => {
          const key = t.toUpperCase();
          const config = PLATFORM_CONFIG[key];
          return config
            ? <Tag key={t} color={config.color} style={{ fontSize: 10, margin: 1 }}>{config.emoji} {config.label}</Tag>
            : <Tag key={t}>{t}</Tag>;
        });
      },
    },
    { title: '구독', dataIndex: 'is_subscribed', width: 70, render: (s) => <Badge status={s ? 'success' : 'error'} text={s ? '구독' : '해지'} /> },
    { title: '카카오', dataIndex: 'kakao_message_agreed', width: 60, render: (agreed, r) => (
      r.kakao_id ? <Tag color={agreed ? 'green' : 'orange'} style={{ fontSize: 10 }}>{agreed ? '✅' : '🔗'}</Tag> : <span style={{color:'#ddd'}}>-</span>
    )},
    { title: '오픈', dataIndex: 'total_emails_opened', width: 55, render: (n) => n || 0, sorter: (a,b) => (a.total_emails_opened||0)-(b.total_emails_opened||0) },
    { title: '클릭', dataIndex: 'total_clicks', width: 55, render: (n) => n || 0 },
    { title: '최근활동', dataIndex: 'last_engaged_at', width: 95, render: (d) => d ? new Date(d).toLocaleDateString('ko-KR') : '-' },
    { title: '', key: 'action', width: 80, render: (_, r) => (
      <Space>
        <Tooltip title="AI 인사이트">
          <Button type="link" icon={<UserOutlined />} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }} />
        </Tooltip>
        <Popconfirm title="삭제?" onConfirm={(e) => { e?.stopPropagation(); handleDelete(r.id); }} onCancel={(e) => e?.stopPropagation()}>
          <Button type="text" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
        </Popconfirm>
      </Space>
    )},
  ];

  // ── 사용자 유형 뱃지 ──
  const UserTypeBadge = ({ type, score }) => {
    const config = USER_TYPE_CONFIG[type] || USER_TYPE_CONFIG.normal;
    return (
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        background: config.bg, border: `1px solid ${config.border}`,
        borderRadius: 20, padding: '4px 14px 4px 10px',
      }}>
        <span style={{ fontSize: 14 }}>{config.label}</span>
        {score != null && (
          <span style={{ fontSize: 12, fontWeight: 600, color: config.color }}>{score}점</span>
        )}
      </div>
    );
  };

  // ── 추천 액션 카드 ──
  const ActionCard = ({ action, index }) => {
    const config = ACTION_TYPE_CONFIG[action.action_type] || { icon: '📋', label: action.action_type, color: '#666' };
    const executed = actionResults[index];
    return (
      <div style={{
        border: executed ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
        borderRadius: 10, padding: 14, marginBottom: 10,
        background: executed ? '#f6ffed' : '#fff', transition: 'all 0.3s',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>{config.icon}</span>
            <div>
              <Text strong style={{ fontSize: 13 }}>{action.title || config.label}</Text>
              <Tag color={action.priority === 'high' ? 'red' : action.priority === 'medium' ? 'orange' : 'default'} style={{ marginLeft: 6, fontSize: 10 }}>{action.priority}</Tag>
            </div>
          </div>
        </div>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>{action.reason}</Text>
        {action.email_subject && (
          <div style={{ background: '#fafafa', borderRadius: 6, padding: '6px 10px', marginBottom: 10, fontSize: 11 }}>
            <Text type="secondary">제목: </Text><Text>{action.email_subject}</Text>
          </div>
        )}
        {executed ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Tag color="success">✓ 실행 완료</Tag>
            <Text style={{ fontSize: 11 }}>캠페인 ID: {executed.campaign_id?.slice(0, 8)}...</Text>
          </div>
        ) : (
          <Space>
            <Button type="primary" size="small" icon={<SendOutlined />} loading={actionLoading === index}
              onClick={() => handleExecuteAction(action, index, true)} style={{ borderRadius: 6 }}>즉시 발송</Button>
            <Button size="small" icon={<RocketOutlined />} loading={actionLoading === index}
              onClick={() => handleExecuteAction(action, index, false)} style={{ borderRadius: 6 }}>캠페인만 생성</Button>
          </Space>
        )}
      </div>
    );
  };

  // ── 인사이트 탭 ──
  const InsightTab = () => {
    if (!insightData) return null;
    const d = insightData;
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <UserTypeBadge type={d.user_type} score={d.engagement_score} />
          <Tooltip title="인사이트 새로고침">
            <Button icon={<ReloadOutlined />} size="small" onClick={() => handleRowClick(selectedContact)} />
          </Tooltip>
        </div>
        <div style={{ background: '#f6f8fa', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <Space direction="vertical" size={4}>
            <Text strong style={{ fontSize: 15 }}>{d.data?.contact?.name || '-'}</Text>
            <Text type="secondary">{d.data?.contact?.email}</Text>
            <Space style={{ marginTop: 4 }}>
              {d.data?.contact?.tags?.map(t => {
                const pKey = t.toUpperCase();
                const pConfig = PLATFORM_CONFIG[pKey];
                return pConfig
                  ? <Tag key={t} color={pConfig.color}>{pConfig.emoji} {pConfig.label}</Tag>
                  : <Tag key={t}>{t}</Tag>;
              })}
              {d.data?.real?.plan && (
                <Tag color={d.data.real.plan === 'admin' ? 'red' : d.data.real.plan === 'free' ? 'default' : 'gold'}>{d.data.real.plan}</Tag>
              )}
            </Space>
            {/* Platform badge */}
            {d.platform && d.platform !== 'REAL' && (
              <Tag color={PLATFORM_CONFIG[d.platform]?.color || '#666'} style={{ marginTop: 4 }}>
                {PLATFORM_CONFIG[d.platform]?.emoji} {PLATFORM_CONFIG[d.platform]?.label || d.platform}
              </Tag>
            )}
          </Space>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
          <div style={{ background: '#e6f7ff', borderRadius: 8, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 'bold', color: '#1890ff' }}>{d.data?.real?.reviews?.length || d.data?.platform_data?.reviews?.length || 0}</div>
            <div style={{ color: '#666', fontSize: 11 }}>리뷰</div>
          </div>
          <div style={{ background: '#f6ffed', borderRadius: 8, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 'bold', color: '#52c41a' }}>{d.data?.real?.talk_count || d.data?.platform_data?.talk_count || 0}</div>
            <div style={{ color: '#666', fontSize: 11 }}>Talk</div>
          </div>
          <div style={{ background: '#fff7e6', borderRadius: 8, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 'bold', color: '#fa8c16' }}>
              {d.data?.campaigns?.filter(c => c.opened).length || 0}/{d.data?.campaigns?.filter(c => c.status === 'SENT' || c.sent).length || 0}
            </div>
            <div style={{ color: '#666', fontSize: 11 }}>캠페인 오픈</div>
          </div>
        </div>
        {d.engagement_score != null && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ fontSize: 12, fontWeight: 500 }}>참여도</Text>
              <Text style={{ fontSize: 12, color: '#888' }}>{d.engagement_score}/100</Text>
            </div>
            <Progress percent={d.engagement_score} strokeColor={d.engagement_score > 70 ? '#52c41a' : d.engagement_score > 40 ? '#faad14' : '#ff4d4f'} showInfo={false} size="small" />
          </div>
        )}
        <div style={{ background: '#fafafa', borderRadius: 10, padding: 16, border: '1px solid #f0f0f0', marginBottom: 16 }}>
          <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag color="purple">AI 인사이트</Tag>{d.cached && <Tag color="green">캐시</Tag>}
            <Button size="small" icon={<ReloadOutlined />} onClick={() => { fetch(`${API_BASE}/insights/${selectedContact.id}?refresh=true`).then(r=>r.json()).then(data=>{setInsightData(data);message.success("AI 재분석 완료")}).catch(()=>message.error("재분석 실패")) }}>재분석</Button>
            <Text type="secondary" style={{ fontSize: 10 }}>{d.generated_at ? new Date(d.generated_at).toLocaleString('ko-KR') : ''}</Text>
          </div>
          <div className="insight-markdown" style={{ fontSize: 13, lineHeight: 1.7 }}>
            <ReactMarkdown>{d.insights}</ReactMarkdown>
          </div>
        </div>
        {d.recommended_actions?.length > 0 && (
          <div>
            <Divider style={{ margin: '12px 0' }}>
              <Space><ThunderboltOutlined style={{ color: '#fa8c16' }} /><Text strong style={{ fontSize: 13 }}>AI 추천 액션</Text></Space>
            </Divider>
            {d.recommended_actions.map((action, idx) => (<ActionCard key={idx} action={action} index={idx} />))}
          </div>
        )}
      </div>
    );
  };

  // ── 이력 탭 ──
  const HistoryTab = () => {
    useEffect(() => { if (activeTab === 'history' && !historyData) loadHistory(); }, [activeTab]);
    if (historyLoading) return <Spin style={{ display: 'block', margin: '40px auto' }} />;
    if (!historyData) return <Text type="secondary">이력 없음</Text>;
    return (
      <div>
        <Text strong style={{ fontSize: 14 }}>인사이트 이력</Text>
        <Timeline style={{ marginTop: 12 }}>
          {historyData.insights?.map((item, i) => {
            const config = USER_TYPE_CONFIG[item.user_type] || USER_TYPE_CONFIG.normal;
            return (
              <Timeline.Item key={i} color={config.color}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Space>
                    <Tag style={{ background: config.bg, color: config.color, border: `1px solid ${config.border}` }}>{config.label}</Tag>
                    <Text>{item.score}점</Text>
                    <Tag>{item.source}</Tag>
                    {item.platform && <Tag color={PLATFORM_CONFIG[item.platform]?.color}>{PLATFORM_CONFIG[item.platform]?.emoji} {item.platform}</Tag>}
                  </Space>
                  <Text type="secondary" style={{ fontSize: 11 }}>{item.created_at ? new Date(item.created_at).toLocaleDateString('ko-KR') : ''}</Text>
                </div>
              </Timeline.Item>
            );
          })}
        </Timeline>
        <Divider />
        <Text strong style={{ fontSize: 14 }}>마케팅 액션 이력</Text>
        <Timeline style={{ marginTop: 12 }}>
          {historyData.actions?.map((item, i) => {
            const config = ACTION_TYPE_CONFIG[item.type] || { icon: '📋', label: item.type };
            return (
              <Timeline.Item key={i} color={item.status === 'completed' ? 'green' : item.status === 'executed' ? 'blue' : 'gray'}>
                <div>
                  <Space>
                    <span>{config.icon}</span><Text>{config.label}</Text>
                    <Tag color={item.status === 'completed' ? 'green' : item.status === 'executed' ? 'blue' : 'default'}>{item.status}</Tag>
                  </Space>
                  {item.executed_at && (<div><Text type="secondary" style={{ fontSize: 11 }}>{new Date(item.executed_at).toLocaleString('ko-KR')}</Text></div>)}
                </div>
              </Timeline.Item>
            );
          })}
          {(!historyData.actions || historyData.actions.length === 0) && (<Text type="secondary">아직 실행된 액션이 없습니다.</Text>)}
        </Timeline>
      </div>
    );
  };

  // ── 일괄 분석 모달 ──
  const BatchModal = () => {
    const platformLabel = selectedPlatform === 'ALL' ? '전체' : PLATFORM_CONFIG[selectedPlatform]?.label;
    return (
      <Modal
        title={<Space><RobotOutlined /> AI 에이전트 일괄 분석 — {platformLabel}</Space>}
        open={batchModalOpen} onCancel={() => setBatchModalOpen(false)} footer={null} width={700}
      >
        <div style={{ marginBottom: 20 }}>
          <Text type="secondary">
            {selectedPlatform === 'ALL' ? '전체' : PLATFORM_CONFIG[selectedPlatform]?.label} 구독자를 AI로 분석하여 사용자 유형을 분류하고,
            이탈위험/휴면 사용자에게 자동으로 마케팅 액션을 생성합니다.
          </Text>
        </div>
        <Button type="primary" size="large" icon={<ThunderboltOutlined />} loading={batchRunning}
          onClick={handleBatchAnalyze} style={{ marginBottom: 20, borderRadius: 8 }} block>
          {batchRunning ? '분석 중...' : `${platformLabel} 일괄 분석 시작`}
        </Button>
        {batchSummary && (
          <div style={{ marginBottom: 20 }}>
            <Text strong>사용자 유형 분포</Text>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginTop: 8 }}>
              {Object.entries(USER_TYPE_CONFIG).map(([key, config]) => {
                const data = batchSummary.user_type_distribution?.[key];
                return (
                  <div key={key} style={{ background: config.bg, border: `1px solid ${config.border}`, borderRadius: 8, padding: 10, textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: config.color }}>{data?.count || 0}</div>
                    <div style={{ fontSize: 11, color: '#666' }}>{config.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <Divider>실행 이력</Divider>
        <div style={{ maxHeight: 300, overflow: 'auto' }}>
          {agentRuns.map(run => (
            <div key={run.id} style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, marginBottom: 8, background: run.status === 'running' ? '#e6f7ff' : '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Space>
                  <Tag color={run.status === 'completed' ? 'green' : run.status === 'running' ? 'blue' : 'red'}>{run.status}</Tag>
                  <Tag>{run.run_type}</Tag>
                  {run.platform && <Tag color={PLATFORM_CONFIG[run.platform]?.color}>{PLATFORM_CONFIG[run.platform]?.emoji} {run.platform}</Tag>}
                </Space>
                <Text type="secondary" style={{ fontSize: 11 }}>{run.started_at ? new Date(run.started_at).toLocaleString('ko-KR') : ''}</Text>
              </div>
              <Space split={<Divider type="vertical" />} style={{ fontSize: 12 }}>
                <span>분석 {run.total_analyzed}명</span>
                <span>🔥 {run.power_users}명</span>
                <span>⚠️ {run.at_risk}명</span>
                <span>액션 {run.actions_created}건</span>
              </Space>
            </div>
          ))}
          {agentRuns.length === 0 && <Text type="secondary">아직 실행 이력이 없습니다.</Text>}
        </div>
      </Modal>
    );
  };

  return (
    <div>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>연락처 관리</h1>
        <Button type="default" icon={<RobotOutlined />}
          onClick={() => { setBatchModalOpen(true); loadBatchSummary(); }}
          style={{ borderRadius: 8 }}>
          AI 일괄 분석 {selectedPlatform !== 'ALL' && `(${PLATFORM_CONFIG[selectedPlatform]?.label})`}
        </Button>
      </div>

      {/* 플랫폼 필터 탭 */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {Object.entries(PLATFORM_CONFIG).map(([key, config]) => {
          const isSelected = selectedPlatform === key;
          const count = platformCounts[key] || 0;
          return (
            <div
              key={key}
              onClick={() => { setSelectedPlatform(key); setPagination(p => ({...p, current: 1})); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
                border: isSelected ? `2px solid ${config.color}` : '1px solid #e8e8e8',
                background: isSelected ? `${config.color}08` : '#fff',
                fontWeight: isSelected ? 600 : 400,
                color: isSelected ? config.color : '#666',
                transition: 'all 0.2s', fontSize: 13,
              }}
            >
              <span>{config.emoji}</span>
              <span>{config.label}</span>
              {count > 0 && (
                <span style={{
                  background: isSelected ? config.color : '#e8e8e8',
                  color: isSelected ? '#fff' : '#666',
                  borderRadius: 10, padding: '0 7px', fontSize: 11, fontWeight: 600,
                }}>{count}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* 검색 + 추가 */}
      <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Input prefix={<SearchOutlined />} placeholder="이름 또는 이메일 검색" value={searchText}
          onChange={e => { setSearchText(e.target.value); setPagination(p => ({...p, current: 1})); }}
          allowClear style={{ width: 280 }} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>추가</Button>
      </Space>

      {/* 테이블 */}
      <Table
        columns={columns} dataSource={contacts} rowKey="id" loading={loading}
        pagination={{ ...pagination, onChange: (page) => setPagination(p => ({...p, current: page})), showTotal: (total) => `${total}명` }}
        onRow={(record) => ({ onClick: () => handleRowClick(record), style: { cursor: 'pointer' } })}
        size="middle"
      />

      {/* 연락처 추가 모달 */}
      <Modal title="연락처 추가" open={isModalOpen} onOk={() => form.submit()} onCancel={() => setIsModalOpen(false)}>
        <Form form={form} onFinish={handleAdd} layout="vertical">
          <Form.Item name="email" label="이메일" rules={[{ required: true, type: 'email' }]}><Input /></Form.Item>
          <Form.Item name="name" label="이름"><Input /></Form.Item>
          <Form.Item name="tags" label="태그"><Select mode="tags" /></Form.Item>
        </Form>
      </Modal>

      {/* 인사이트 드로어 */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <UserOutlined />
            <span>{selectedContact?.name || selectedContact?.email || '사용자'} 인사이트</span>
          </div>
        }
        placement="right" width={620} onClose={() => setDrawerOpen(false)} open={drawerOpen}
      >
        {insightLoading ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, color: '#888' }}>AI가 사용자 데이터를 분석 중입니다...</p>
          </div>
        ) : insightData ? (
          <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
            { key: 'insight', label: <span><ThunderboltOutlined /> 인사이트</span>, children: <InsightTab /> },
            { key: 'history', label: <span><HistoryOutlined /> 이력</span>, children: <HistoryTab /> },
          ]} />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>데이터를 불러올 수 없습니다.</div>
        )}
      </Drawer>

      <BatchModal />
    </div>
  );
};

export default Contacts;
